import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Input, InputGroup, VStack, Text, Button, Image, Flex, Spinner } from '@chakra-ui/react'
import { Icon } from '../icon'
import theme from '../../styles'
import { useTranslation, Trans } from 'react-i18next'
import { addAnswer, selectAnswers, selectCurrentStep, selectScenario, useDispatch, useSelector } from '../../state'
import { Answers, cardImageMap, ScenarioType, Steps } from '../../state/interfaces'
import { CheckboxConfirm } from '../checkboxes/checkbox-confirm'
import { useColorModeValue } from '@chakra-ui/react'
import { checkGmailTypo } from '../../utils/emailHelpers'
import { VALID_COMPANY_NAME_REGEX, VALID_EMAIL_REGEX, VALID_USER_NAME_REGEX } from '../../utils/regex'
import { isTheDomainProtegezVousWhitelabel } from '../../whitelabels/whitelabel-domains'

export interface InputDefaultProps {
  inputType?: string
  name: string
  placeholder?: string
  onChange?: (values: string[]) => void
  onSubmittingChange?: (isSubmitting: boolean) => void
  grandImage?: string
}

export const InputDefault: FC<InputDefaultProps> = ({
  placeholder,
  name,
  onChange,
  onSubmittingChange,
  grandImage,
  inputType,
}) => {
  const { t } = useTranslation()
  const inputElement = useRef<HTMLInputElement>(null)
  const scenario = useSelector(selectScenario)
  const image = useMemo(() => (grandImage ? cardImageMap[grandImage] : ''), [grandImage])
  const shouldShowOptionalConfirmation = !isTheDomainProtegezVousWhitelabel(window.location.hostname)
  const [inputValue, setInputValue] = useState<string>('')
  const [checkBoxIsChecked, setCheckBoxIsChecked] = useState(false)
  const [needConsent, setNeedConsent] = useState(false)
  // const scenario = useSelector(selectScenario)
  const answers: Answers = useSelector(selectAnswers)
  const isValidEmail = useCallback((rawEmail: string): boolean => {
    const matches = rawEmail.match(VALID_EMAIL_REGEX)

    return matches !== null && matches.length > 0
  }, [])

  const isValidName = useCallback((rawName: string): boolean => {
    const matches = rawName.match(VALID_USER_NAME_REGEX)

    return matches !== null && matches.length > 0
  }, [])

  const isValidCompanyName = useCallback((rawName: string): boolean => {
    const matches = rawName.match(VALID_COMPANY_NAME_REGEX)

    return matches !== null && matches.length > 0
  }, [])

  const isValidLength = useCallback((rawName: string): boolean => {
    return rawName.length > 0 && rawName.length <= 40
  }, [])

  const isValidLengthCompanyName = useCallback((rawName: string): boolean => {
    return rawName.length > 0 && rawName.length <= 100
  }, [])

  const isValid = isValidEmail(inputValue)
  const isValidNameInput = isValidName(inputValue)
  const isValidCompanyInput = isValidCompanyName(inputValue)
  const isValidLengthInput = isValidLength(inputValue)
  const isValidLengthCompanyNameInput = isValidLengthCompanyName(inputValue)
  const invalidEmailMessage = t('error_invalid-email')
  const mustConfirmEmailMessage = t('error_must-confirm-email-authorization')
  const invalidNameMessage = t('error_invalid-name')
  const invalidCompanyNameMessage = t('error_invalid-company-name')
  const invalidLengthMessage = t('error_invalid-length')
  const invalidLengthCompanyNameMessage = t('error_invalid-company-length')
  const bgSuggestion = useColorModeValue('yellow.50', '#4a3d00')
  const borderColorSuggestion = useColorModeValue('yellow.500', '#ffe566')

  const formatPhoneNumber = (inputValue: string) => {
    if (!inputValue) return inputValue

    const phoneNumber = inputValue.replace(/[^\d]/g, '')

    const phoneNumberLength = phoneNumber.length

    if (phoneNumberLength < 4) return phoneNumber

    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`
    }

    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`
  }

  const currentStep = useSelector(selectCurrentStep)
  const [inputInUse, setInputInUse] = useState(false)
  // const [selectedChoices, setSelectedChoices] = useState<string[]>([])
  const [isConfirmedEmail, setIsConfirmedEmail] = useState(false)
  const [mustConfirmEmail, setMustConfirmEmail] = useState(false)
  const [isConfirmedPrice, setIsConfirmedPrice] = useState(false)
  const [isConfirmedNewsletter, setIsConfirmedNewsletter] = useState(false)

  const [emailTypo, setEmailTypo] = useState<{
    hasTypo: boolean
    suggestedEmail?: string
  }>({ hasTypo: false })
  const [clientInfoName, setClientInfoName] = useState('')
  const [clientInfoPhone, setClientInfoPhone] = useState('')
  const [clientInfoEmail, setClientInfoEmail] = useState('')
  const [clientInfoActiveField, setClientInfoActiveField] = useState<'name' | 'phone' | 'email' | null>(null)
  const [clientInfoEmailTypo, setClientInfoEmailTypo] = useState<{
    hasTypo: boolean
    suggestedEmail?: string
  }>({ hasTypo: false })
  const [clientInfoIsSubmitting, setClientInfoIsSubmitting] = useState(false)
  const clientInfoSubmitTimeouts = useRef<Array<ReturnType<typeof setTimeout>>>([])

  const handleCheckboxChange = (value: string, checked: boolean) => {
    if (value === 'confirm-newsletters') {
      setIsConfirmedNewsletter(checked)
    } else if (value === 'confirm-alert') {
      setIsConfirmedPrice(checked)
    }
  }

  useEffect(() => {
    if (isConfirmedPrice && (!shouldShowOptionalConfirmation || isConfirmedNewsletter)) {
      setIsConfirmedEmail(true)
    } else {
      setIsConfirmedEmail(false)
    }
  }, [isConfirmedNewsletter, isConfirmedPrice, shouldShowOptionalConfirmation])

  useEffect(() => {
    const emailTypes = [
      'email',
      'newsletter-email',
      'mobile-search-email',
      'email-page-go',
      'email-decision-review-contest',
    ]
    if (!inputType || !emailTypes.includes(inputType)) return

    const delay = setTimeout(() => {
      if (isValidEmail(inputValue)) {
        const typoResult = checkGmailTypo(inputValue)
        setEmailTypo(typoResult)
      } else {
        setEmailTypo({ hasTypo: false })
      }
    }, 400)

    return () => clearTimeout(delay)
  }, [inputValue, inputType, isValidEmail])

  useEffect(() => {
    if (inputType !== 'client-info-internet') return

    const delay = setTimeout(() => {
      if (isValidEmail(clientInfoEmail)) {
        setClientInfoEmailTypo(checkGmailTypo(clientInfoEmail))
      } else {
        setClientInfoEmailTypo({ hasTypo: false })
      }
    }, 400)

    return () => clearTimeout(delay)
  }, [clientInfoEmail, inputType, isValidEmail])

  useEffect(() => {
    return () => {
      clientInfoSubmitTimeouts.current.forEach(clearTimeout)
    }
  }, [])

  useEffect(() => {
    if (inputType !== 'client-info-internet') return

    onSubmittingChange?.(clientInfoIsSubmitting)
  }, [clientInfoIsSubmitting, inputType, onSubmittingChange])

  const dispatch = useDispatch()

  const setNewsletterConfirmation = () => {
    dispatch(
      addAnswer({
        stepId: Steps.CONFIRM_NEWSLETTER,
        answerIds: ['yes'],
        hidden: true,
      }),
    )
  }

  const setAlertConfirmation = () => {
    dispatch(
      addAnswer({
        stepId: Steps.CONFIRM_ALERT,
        answerIds: ['yes'],
        hidden: true,
      }),
    )
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value
    setInputValue(value)
    setInputInUse(true)
  }

  const acceptSuggestion = () => {
    if (emailTypo.suggestedEmail) {
      setInputValue(emailTypo.suggestedEmail)
      setEmailTypo({ hasTypo: false })
    }
  }

  const acceptClientInfoSuggestion = () => {
    if (clientInfoEmailTypo.suggestedEmail) {
      setClientInfoEmail(clientInfoEmailTypo.suggestedEmail)
      setClientInfoEmailTypo({ hasTypo: false })
    }
  }

  const clientInfoNameIsValid = isValidName(clientInfoName.trim())
  const clientInfoNameLengthIsValid = isValidLength(clientInfoName.trim())
  const clientInfoEmailIsValid = isValidEmail(clientInfoEmail.trim())
  const canSubmitClientInfoInternet =
    clientInfoNameIsValid && clientInfoNameLengthIsValid && clientInfoEmailIsValid && clientInfoPhone.length === 14

  const submitClientInfoInternet = () => {
    if (!canSubmitClientInfoInternet || clientInfoIsSubmitting) return

    setClientInfoIsSubmitting(true)
    dispatch(
      addAnswer({
        stepId: Steps.EMAIL_ADDRESS,
        answerIds: [clientInfoEmail.trim()],
        hidden: true,
      }),
    )

    clientInfoSubmitTimeouts.current.push(
      setTimeout(() => {
        dispatch(
          addAnswer({
            stepId: Steps.FIRST_NAME,
            answerIds: [clientInfoName.trim()],
            hidden: true,
          }),
        )
      }, 3000),
    )

    clientInfoSubmitTimeouts.current.push(
      setTimeout(() => {
        dispatch(
          addAnswer({
            stepId: Steps.PHONE_NUMBER,
            answerIds: [clientInfoPhone],
          }),
        )
      }, 3100),
    )
  }

  if (inputType === 'client-info-internet') {
    if (clientInfoIsSubmitting) {
      return (
        <Flex minH="280px" width="full" alignItems="center" justifyContent="center">
          <VStack spacing={4}>
            <Spinner color="green.500" size="xl" thickness="4px" />
            <Text color="darkGrey" fontSize={{ base: 20, md: 24 }} fontWeight="bold">
              {t('client-info-internet-analysing')}
            </Text>
          </VStack>
        </Flex>
      )
    }

    return (
      <VStack spacing={5} marginY={8} justifyContent={'left'} width={'full'}>
        <Box width={{ base: '100%', md: '600px' }}>
          <InputGroup layerStyle={clientInfoActiveField === 'name' ? theme.LayerStyles.INPUT_GROUP : ''}>
            <Input
              aria-label={t('client-info-internet-name_placeholder')}
              placeholder={t('client-info-internet-name_placeholder')}
              value={clientInfoName}
              variant="unstyled"
              h="68px"
              pl="16px"
              pr="16px"
              w="100%"
              fontSize={{ base: 18, md: 22 }}
              layerStyle={theme.LayerStyles.INPUT}
              autoFocus
              name={`${name}-name`}
              backgroundColor="transparent"
              _placeholder={{ color: 'ashGrey' }}
              onClick={(e) => {
                e.stopPropagation()
                e.currentTarget.focus()
                setClientInfoActiveField('name')
              }}
              onBlur={() => setClientInfoActiveField(null)}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setClientInfoName(event.target.value)
                setClientInfoActiveField('name')
              }}
              onKeyPress={(event) => {
                if (event.key === 'Enter' && canSubmitClientInfoInternet) {
                  event.preventDefault()
                  submitClientInfoInternet()
                }
              }}
            />
          </InputGroup>
          {clientInfoName && !clientInfoNameIsValid && (
            <Text color={'red'} fontSize="xs" padding={2}>
              {invalidNameMessage}
            </Text>
          )}
          {clientInfoName && clientInfoNameIsValid && !clientInfoNameLengthIsValid && (
            <Text color={'red'} fontSize="xs" padding={2}>
              {invalidLengthMessage}
            </Text>
          )}
        </Box>

        <Box width={{ base: '100%', md: '600px' }}>
          <InputGroup layerStyle={clientInfoActiveField === 'phone' ? theme.LayerStyles.INPUT_GROUP : ''}>
            <Input
              aria-label={t('phone-number_answerPlaceholder')}
              placeholder={t('phone-number_answerPlaceholder')}
              value={clientInfoPhone}
              variant="unstyled"
              h="68px"
              pl="16px"
              pr="16px"
              w="100%"
              fontSize={{ base: 18, md: 22 }}
              layerStyle={theme.LayerStyles.INPUT}
              name={`${name}-phone`}
              backgroundColor="transparent"
              _placeholder={{ color: 'ashGrey' }}
              onClick={(e) => {
                e.stopPropagation()
                e.currentTarget.focus()
                setClientInfoActiveField('phone')
              }}
              onBlur={() => setClientInfoActiveField(null)}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setClientInfoPhone(formatPhoneNumber(event.target.value))
                setClientInfoActiveField('phone')
              }}
              onKeyPress={(event) => {
                if (event.key === 'Enter' && canSubmitClientInfoInternet) {
                  event.preventDefault()
                  submitClientInfoInternet()
                }
              }}
            />
          </InputGroup>
        </Box>

        <Box width={{ base: '100%', md: '600px' }}>
          <InputGroup layerStyle={clientInfoActiveField === 'email' ? theme.LayerStyles.INPUT_GROUP : ''}>
            <Input
              aria-label={t('email-address_answerPlaceholder')}
              placeholder={t('email-address_answerPlaceholder')}
              value={clientInfoEmail}
              variant="unstyled"
              h="68px"
              pl="16px"
              pr="65px"
              w="100%"
              fontSize={{ base: 18, md: 22 }}
              layerStyle={theme.LayerStyles.INPUT}
              name={`${name}-email`}
              backgroundColor="transparent"
              _placeholder={{ color: 'ashGrey' }}
              onClick={(e) => {
                e.stopPropagation()
                e.currentTarget.focus()
                setClientInfoActiveField('email')
              }}
              onBlur={() => setClientInfoActiveField(null)}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setClientInfoEmail(event.target.value)
                setClientInfoActiveField('email')
              }}
              onKeyPress={(event) => {
                if (event.key === 'Enter' && canSubmitClientInfoInternet) {
                  event.preventDefault()
                  submitClientInfoInternet()
                }
              }}
            />
            <Button
              role={'button'}
              position="absolute"
              borderRadius="md"
              variant="unstyled"
              layerStyle={theme.LayerStyles.BTN_INPUT_PILL}
              onClick={submitClientInfoInternet}
              isDisabled={!canSubmitClientInfoInternet || clientInfoIsSubmitting}
              _disabled={{
                backgroundColor: 'grey',
                opacity: 1,
                cursor: 'not-allowed',
              }}
            >
              <Icon icon="arrow-right" color="white" />
            </Button>
          </InputGroup>
          {clientInfoEmailTypo.hasTypo && clientInfoEmailTypo.suggestedEmail && (
            <Box
              mt={3}
              p={4}
              bg={bgSuggestion}
              borderRadius="md"
              borderLeft="4px solid"
              borderColor={borderColorSuggestion}
              cursor="pointer"
              onClick={acceptClientInfoSuggestion}
            >
              <Text fontSize="xs" mt={1}>
                <Trans
                  i18nKey="email_typo_autocorrected_click"
                  values={{ email: clientInfoEmailTypo.suggestedEmail }}
                  components={{
                    strong: <strong style={{ color: 'green', fontWeight: 600, textDecoration: 'underline' }} />,
                  }}
                />
              </Text>
            </Box>
          )}
          {clientInfoEmail && !clientInfoEmailIsValid && (
            <Text color={'red'} fontSize="xs" padding={2}>
              {invalidEmailMessage}
            </Text>
          )}
        </Box>

        {image && <Image src={image} alt="image" width={['200px', '250px']} height={['200px', '250px']} />}
      </VStack>
    )
  }

  return (
    <VStack spacing={5} marginY={8} justifyContent={'left'} width={'full'}>
      <Box>
        <InputGroup
          minWidth={currentStep?.id === Steps.EMAIL_ADDRESS_LEAD ? { base: '100%', md: '600px' } : undefined}
          layerStyle={inputInUse ? theme.LayerStyles.INPUT_GROUP : ''}
        >
          <Input
            ref={inputElement}
            aria-label={placeholder}
            placeholder={placeholder}
            value={inputValue}
            variant="unstyled"
            h="68px"
            pl="16px"
            pr="65px"
            w="100%"
            fontSize={{ base: 18, md: 22 }}
            layerStyle={theme.LayerStyles.INPUT}
            autoFocus
            name={name}
            backgroundColor="transparent"
            _placeholder={{ color: 'ashGrey' }}
            onClick={(e) => {
              e.stopPropagation()
              e.currentTarget.focus()
              setInputInUse(true)
            }}
            onBlur={() => setInputInUse(false)}
            onChange={
              inputType && inputType === 'phone'
                ? (event: React.ChangeEvent<HTMLInputElement>): void =>
                    setInputValue(formatPhoneNumber(event.target.value))
                : handleInputChange
            }
            onKeyPress={(ev) => {
              if (
                ev.key === 'Enter' &&
                ((inputValue && inputType && inputType === 'email' && isValid) ||
                  (inputValue && inputType && inputType === 'phone' && inputValue.length === 14) ||
                  (inputValue && inputType && inputType === 'name' && isValidNameInput && isValidLengthInput) ||
                  (inputValue &&
                    inputType &&
                    inputType === 'companyName' &&
                    isValidCompanyInput &&
                    isValidLengthCompanyNameInput) ||
                  (inputValue && inputType === null))
              ) {
                ev.preventDefault()
                onChange && onChange([inputValue])
              }
            }}
            onKeyUp={() => {
              inputValue && inputValue.length >= 0 ? setNeedConsent(true) : setNeedConsent(false)
            }}
            onMouseEnter={() => {
              setInputInUse(true)
            }}
            onMouseLeave={() => {
              inputElement!.current!.value === '' ? setInputInUse(false) : setInputInUse(true)
            }}
          />
          {((inputValue && inputType && inputType === 'email' && isValid) ||
            (inputValue && inputType && inputType === 'email-decision-review-contest' && isValid) ||
            (inputValue && inputType && inputType === 'newsletter-email' && isValid) ||
            (inputValue && inputType && inputType === 'phone' && inputValue.length === 14) ||
            (inputValue && inputType === null) ||
            (inputValue && inputType && inputType === 'name' && isValidNameInput && isValidLengthInput) ||
            (inputValue &&
              inputType &&
              inputType === 'companyName' &&
              isValidCompanyInput &&
              isValidLengthCompanyNameInput)) && (
            <Button
              role={'button'}
              position="absolute"
              borderRadius="md"
              variant="unstyled"
              layerStyle={theme.LayerStyles.BTN_INPUT_PILL}
              onClick={() => {
                onChange && onChange([inputValue])
              }}
            >
              <Icon icon="arrow-right" color="white" />
            </Button>
          )}
          {inputValue && inputType && inputType === 'email-page-go' && isValid && (
            <Button
              role={'button'}
              position="absolute"
              borderRadius="md"
              variant="unstyled"
              layerStyle={theme.LayerStyles.BTN_INPUT_PILL}
              onClick={() => {
                if (isConfirmedEmail) {
                  setMustConfirmEmail(false)
                  if (shouldShowOptionalConfirmation) {
                    setNewsletterConfirmation()
                  }
                  setAlertConfirmation()
                  onChange && onChange([inputValue])
                } else {
                  setMustConfirmEmail(true)
                }
              }}
            >
              {answers && answers['email-address'] && answers['email-address'][0] ? (
                <Spinner />
              ) : (
                <Icon icon="arrow-right" color="white" />
              )}
            </Button>
          )}
        </InputGroup>
        {/* Gmail typo suggestion alert */}
        {emailTypo.hasTypo && emailTypo.suggestedEmail && (
          <Box
            mt={3}
            mb={6}
            p={4}
            bg={bgSuggestion}
            borderRadius="md"
            borderLeft="4px solid"
            borderColor={borderColorSuggestion}
            width="100%"
            maxWidth={currentStep?.id === Steps.EMAIL_ADDRESS_LEAD ? { base: '100%', md: '600px' } : undefined}
            cursor="pointer"
            onClick={acceptSuggestion}
          >
            <Text fontSize="xs" mt={1}>
              <Trans
                i18nKey="email_typo_autocorrected_click"
                values={{ email: emailTypo.suggestedEmail }}
                components={{
                  strong: <strong style={{ color: 'green', fontWeight: 600, textDecoration: 'underline' }} />,
                }}
              />
            </Text>
          </Box>
        )}

        {inputValue &&
          inputType &&
          (inputType === 'email' || inputType === 'mobile-search-email' || inputType === 'email-page-go') &&
          !isValid && (
            <Text color={'red'} fontSize="xs" padding={2}>
              {invalidEmailMessage}
            </Text>
          )}
        {inputValue && inputType && inputType === 'name' && !isValidNameInput && (
          <Text color={'red'} fontSize="xs" padding={2}>
            {invalidNameMessage}
          </Text>
        )}
        {inputValue && inputType && inputType === 'companyName' && !isValidCompanyInput && (
          <Text color={'red'} fontSize="xs" padding={2}>
            {invalidCompanyNameMessage}
          </Text>
        )}
        {inputValue && inputType && inputType === 'name' && isValidNameInput && !isValidLengthInput && (
          <Text color={'red'} fontSize="xs" padding={2}>
            {invalidLengthMessage}
          </Text>
        )}
        {inputValue &&
          inputType &&
          inputType === 'companyName' &&
          isValidCompanyInput &&
          !isValidLengthCompanyNameInput && (
            <Text color={'red'} fontSize="xs" padding={2}>
              {invalidLengthCompanyNameMessage}
            </Text>
          )}
        {!isConfirmedEmail && isValid && mustConfirmEmail && (
          <Text color={'red'} fontSize="xs" padding={2}>
            {mustConfirmEmailMessage}
          </Text>
        )}
      </Box>
      {inputType && inputType === 'mobile-search-email' && needConsent && (
        <label style={{ fontSize: '1rem' }}>
          <input
            onClick={() => setCheckBoxIsChecked(!checkBoxIsChecked)}
            type="checkbox"
            name="checkbox"
            defaultChecked={checkBoxIsChecked}
            style={{ marginRight: '0.6rem' }}
          />
          {t('common_email-communication-consent')}
        </label>
      )}
      {inputType &&
        inputType === 'email-page-go' && ( //needConsent && (
          // <Flex direction="column" marginY={5} maxW={{ md: 600 }}>
          //   <label>
          //     <Box backgroundColor={'#e9f8e5'} borderRadius={6} marginTop={4}>
          //       <Box display="flex" width="100%" paddingX={6} paddingY={2}>
          //         <Flex alignItems="center" minW={{ base: 120, md: 200 }}>
          //           <Flex border="1px solid lightgrey" alignItems="center" mt={1} minW={'18px'} zIndex={2}>
          //             <CheckboxConfirm value="confirm-alert" onChange={handleCheckboxChange} />
          //           </Flex>
          //           <Text ml={4} whiteSpace="normal" cursor="pointer" fontSize={{ base: '14', md: '16' }}>
          //             <strong>
          //               {t('mandatory-confirmation-title')}{' '}
          //               <span style={{ color: 'red', textTransform: 'uppercase' }}>*</span>
          //             </strong>
          //             <br />
          //             {t('mandatory-confirmation-text')}
          //           </Text>
          //         </Flex>
          //       </Box>
          //     </Box>
          //   </label>
          // </Flex>
          <Flex direction="column" marginY={5} maxW={{ md: 600 }}>
            {shouldShowOptionalConfirmation && (
              <label>
                <Box border="1px solid lightgrey" borderRadius={6}>
                  <Box display="flex" width="100%" paddingX={6} paddingY={2}>
                    <Flex alignItems="center" minW={{ base: 120, md: 200 }}>
                      <Flex border="1px solid lightgrey" alignItems="center" mt={1} minW={'18px'} zIndex={2}>
                        <CheckboxConfirm
                          value="confirm-newsletters"
                          onChange={handleCheckboxChange}
                          // isDisabled={!isConfirmedPrice && !isAwardScenario}
                          // isChecked={isAwardScenario}
                        />
                      </Flex>
                      <Text ml={4} whiteSpace="normal" cursor="pointer" fontSize={{ base: '14', md: '16' }}>
                        <strong>
                          {t('mandatory-confirmation-title')}{' '}
                          <span style={{ color: 'red', textTransform: 'uppercase' }}>*</span>
                        </strong>
                        <br />
                        {t('optional-confirmation-text')}
                      </Text>
                    </Flex>
                  </Box>
                </Box>
              </label>
            )}
            <label>
              <Box
                // backgroundColor={!isConfirmedPrice && !isAwardScenario ? '#a3a3a3' : ''}
                border="1px solid lightgrey"
                borderRadius={6}
                mt={4}
                // opacity={isConfirmedPrice || isAwardScenario ? 1 : 0.8}
                // pointerEvents={isConfirmedPrice || isAwardScenario ? 'auto' : 'none'}
              >
                <Box display="flex" width="100%" paddingX={6} paddingY={2}>
                  <Flex alignItems="center" minW={{ base: 120, md: 200 }}>
                    <Flex border="1px solid lightgrey" alignItems="center" mt={1} minW={'18px'} zIndex={2}>
                      <CheckboxConfirm value="confirm-alert" onChange={handleCheckboxChange} />
                    </Flex>
                    <Text ml={4} whiteSpace="normal" cursor="pointer" fontSize={{ base: '14', md: '16' }}>
                      <strong>
                        {t('mandatory-confirmation-title')}{' '}
                        <span style={{ color: 'red', textTransform: 'uppercase' }}>*</span>
                      </strong>
                      <br />
                      {t('mandatory-confirmation-text')}
                    </Text>
                  </Flex>
                </Box>
              </Box>
            </label>
          </Flex>
        )}
      {inputType && inputType === 'mobile-search-email' && (
        <Button
          borderRadius="xl"
          onClick={() => {
            onChange && onChange([inputValue])
          }}
          backgroundColor="green.200"
          fontSize={22}
          padding={6}
          color="white"
          fontWeight="bold"
          isDisabled={needConsent ? !isValid || !checkBoxIsChecked : false}
        >
          {t('common_show-results')}
        </Button>
      )}
      {scenario?.type === ScenarioType.DECISION_REVIEW_CONTEST && image ? (
        <Image src={image} alt="image" width={['150px', '200px']} height={['150px', '200px']} />
      ) : (
        image && <Image src={image} alt="image" width={['200px', '250px']} height={['200px', '250px']} />
      )}
      {/* {scenario && (scenario.type === ScenarioType.EMAIL_PAGE_GO_INTERNET || scenario.type === ScenarioType.EMAIL_PAGE_GO_MOBILE) && !isValid && (
        <Flex justifyContent="flex-end">
          {answers && answers['email-address'] && answers['email-address'][0] ? (
            <Spinner marginTop="2em" />
          ) : (
            <Button
              borderRadius="full"
              onClick={() => {
                onChange && onChange(['skipped'])
              }}
              marginTop="0.5em"
              textColor="white"
              fontSize={20}
              backgroundColor="green.600"
            >
              {t('common_skip')}
            </Button>
          )}
        </Flex>
      )} */}
    </VStack>
  )
}
